import { CREATE_CONVERSATION_API } from '~/routes/remix-api.chat.create-conversation';
import { CREATE_CONVERSATION_MESSAGE_API } from '~/routes/remix-api.chat.create-conversation-message';
import { MARK_CONVERSATION_READ_API } from '~/routes/remix-api.chat.mark-conversation-read';
import { UPDATE_CONVERSATION_API } from '~/routes/remix-api.chat.update-conversation';
import { UPDATE_CONVERSATION_THUMBNAIL_API } from '~/routes/remix-api.chat.update-thumbnail';
import { LEAVE_CONVERSATION_API } from '~/routes/remix-api.chat.leave-conversation';
import { ADD_PARTICIPANTS_API } from '~/routes/remix-api.chat.add-participants';
import { REMOVE_PARTICIPANT_API } from '~/routes/remix-api.chat.remove-participant';

/** Sync saved module API route - doesn't need revalidation since it updates in place */
const SYNC_SAVED_MODULE_API = '/remix-api/page-builder/sync-saved-module';

/**
 * Routes that should not trigger a revalidation when the form is submitted.
 */
const NOT_REVALIDATE_ROUTES = [
    CREATE_CONVERSATION_API,
    CREATE_CONVERSATION_MESSAGE_API,
    MARK_CONVERSATION_READ_API,
    UPDATE_CONVERSATION_API,
    UPDATE_CONVERSATION_THUMBNAIL_API,
    LEAVE_CONVERSATION_API,
    ADD_PARTICIPANTS_API,
    REMOVE_PARTICIPANT_API,
    SYNC_SAVED_MODULE_API,
] as const;

/**
 * Same as above, but only for the root _app.tsx route.
 */
const ROOT_NOT_REVALIDATE_ROUTES = NOT_REVALIDATE_ROUTES.filter((route) => {
    return ![
        // Mark conversation read will update the notification count.
        MARK_CONVERSATION_READ_API,
        // Leave conversation may update notification count.
        LEAVE_CONVERSATION_API,
    ].includes(route);
});

/**
 * Check if a route loader should trigger a revalidation when a form is
 * submitted. If the form action is in a predefined list, the loader should not
 * trigger a revalidation. This is a performance optimization to avoid
 * unnecessary revalidations.
 * @param formAction The form action (route) to check.
 * @returns The route loader should not revalidate.
 * @see {@link shouldNotRevalidateRoot} for the root `_app.tsx` route.
 */
export function shouldNotRevalidate(formAction: string | undefined) {
    if (!formAction) {
        return false;
    }
    // Use substring matching to handle query params and variations
    return NOT_REVALIDATE_ROUTES.some((route) => formAction.includes(route));
}

/**
 * Check if the root `_app.tsx` route loader should trigger a revalidation when
 * a form is submitted. This is the same as `shouldNotRevalidate`, but only for
 * the root `_app.tsx` route.
 * @param formAction The form action (route) to check.
 * @returns The root `_app.tsx` route loader should not revalidate.
 * @see {@link shouldNotRevalidate} for routes except for the root `_app.tsx` route.
 */
export function shouldNotRevalidateRoot(formAction: string | undefined) {
    if (!formAction) {
        return false;
    }
    // Use substring matching to handle query params and variations
    return ROOT_NOT_REVALIDATE_ROUTES.some((route) =>
        formAction.includes(route)
    );
}
